import classnames from 'classnames'
import PropTypes from 'prop-types'
import React, { useEffect, useRef } from 'react'
import PerfectScrollbar from 'react-perfect-scrollbar'
import ReactResponsiveModal from 'react-responsive-modal'

import './Modal.scss'

export const MODAL_SIZE_SM = 'S'
export const MODAL_SIZE_MD = 'M'
export const MODAL_SIZE_LG = 'L'
export const MODAL_SIZE_XL = 'XL'
export const MODAL_SIZE_XXL = 'XXL'

export const ModalHead = ({ children, title }) => {
  return (
    <div className='b-modal__head'>
      {title && <h2 className='b-modal__title'>{title}</h2>}
      {children}
    </div>
  )
}

export const ModalBody = ({
  children,
  scrollable,
  className,
  scrollableHeight
}) => {
  const scrollRef = useRef(null)

  useEffect(() => {
    scrollRef.current && scrollRef.current.updateScroll()
  }, [])

  const style = scrollableHeight ? { maxHeight: scrollableHeight } : undefined

  return scrollable ? (
    <PerfectScrollbar
      className={classnames(
        'b-modal__body b-modal__body--scrollable',
        className
      )}
      ref={scrollRef}
      style={style}
    >
      {children}
    </PerfectScrollbar>
  ) : (
    <div className='b-modal__body'>{children}</div>
  )
}

export const ModalFoot = ({ children }) => {
  return <div className='b-modal__foot'>{children}</div>
}

export const Modal = ({
  children,
  className,
  containerClassName,
  overlayClassName,
  rootClassName,
  closeOnOverlayClick,
  onClose,
  show,
  showClose,
  size,
  'data-mu-test': dataAttributeTest
}) => {
  return (
    <ReactResponsiveModal
      open={show}
      center
      classNames={{
        root: classnames('b-modal-root', rootClassName),
        overlay: classnames('b-modal-backdrop', overlayClassName),
        modalContainer: classnames('b-modal-container', containerClassName),
        modal: classnames('b-modal', className, {
          'b-modal--sm': size === MODAL_SIZE_SM,
          'b-modal--md': size === MODAL_SIZE_MD,
          'b-modal--lg': size === MODAL_SIZE_LG,
          'b-modal--xl': size === MODAL_SIZE_XL,
          'b-modal--xxl': size === MODAL_SIZE_XXL
        }),
        closeButton: 'b-modal__close',
        transitionEnter: 'fade-in-down-little', // TODO
        transitionExit: ''
      }}
      onClose={onClose}
      showCloseIcon={showClose}
      closeOnOverlayClick={closeOnOverlayClick}
    >
      <div data-mu-test={classnames('modal', dataAttributeTest)}>
        {children}
      </div>
    </ReactResponsiveModal>
  )
}

Modal.defaultProps = {
  closeOnOverlayClick: true,
  showClose: true,
  size: MODAL_SIZE_MD
}

Modal.propTypes = {
  children: PropTypes.node,
  className: PropTypes.string,
  containerClassName: PropTypes.string,
  overlayClassName: PropTypes.string,
  rootClassName: PropTypes.string,
  closeOnOverlayClick: PropTypes.bool,
  onClose: PropTypes.func,
  show: PropTypes.bool,
  showClose: PropTypes.bool,
  size: PropTypes.oneOf([
    MODAL_SIZE_SM,
    MODAL_SIZE_MD,
    MODAL_SIZE_LG,
    MODAL_SIZE_XL,
    MODAL_SIZE_XXL
  ]),
  /**
   * Additional values for data-attribute used for tests and tracking
   **/
  'data-mu-test': PropTypes.string
}

ModalHead.propTypes = {
  children: PropTypes.node,
  title: PropTypes.string
}

ModalBody.propTypes = {
  children: PropTypes.node,
  scrollable: PropTypes.bool,
  className: PropTypes.string,
  scrollableHeight: PropTypes.string
}

ModalFoot.propTypes = {
  children: PropTypes.node
}
